import { Tooltip, type TooltipProps } from '@mui/material';
import { TinyVariant } from '@shared/constants/components/tinyUtils';
import { TBorderRadius } from '@shared/constants/Styles';
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ComponentType, ReactElement } from 'react';
import { NavLink, NavLinkProps } from 'react-router';
import { IconName } from '../images/IconMap';
import Icons from '../images/Icons';
import SrOnlyText from '../text/SrOnlyText';
import { BoldTinyText, TinyButtonRow, TinyText } from './CommonTinyStyles';

type TinyButtonCommonProps = {
    title: string;
    value?: string | number;
    hideLabel?: boolean;
    tooltip?: string;
    tooltipPlacement?: TooltipProps['placement'];
    icon?: IconName;
    endIcon?: IconName;
    iconSize?: number;
    variant?: TinyVariant;
    borderRadius?: TBorderRadius;
};

type TinyPressRowProps = ButtonHTMLAttributes<HTMLButtonElement> & {
    $variant?: TinyVariant;
    $borderRadius?: TBorderRadius;
};

type TinyLinkRowProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
    $variant?: TinyVariant;
    $borderRadius?: TBorderRadius;
};

type TinyNavRowProps = NavLinkProps & {
    $variant?: TinyVariant;
    $borderRadius?: TBorderRadius;
};

type TinyPressProps = TinyButtonCommonProps &
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof TinyButtonCommonProps | 'href' | 'to'> & {
        href?: never;
        to?: never;
    };

type TinyLinkProps = TinyButtonCommonProps &
    Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof TinyButtonCommonProps | 'href' | 'to'> & {
        href: string;
        to?: never;
    };

type TinyNavProps = TinyButtonCommonProps &
    Omit<NavLinkProps, keyof TinyButtonCommonProps | 'href' | 'to'> & {
        to: NavLinkProps['to'];
        href?: never;
    };

type TinyButtonProps = TinyPressProps | TinyLinkProps | TinyNavProps;

export default function TinyButton(props: Readonly<TinyButtonProps>) {
    if (props.href !== undefined) {
        return <TinyLinkButton {...props} />;
    }
    if (props.to !== undefined) {
        return <TinyNavButton {...props} />;
    }

    return <TinyPressButton {...props} />;
}

function TinyPressButton({
    title,
    value,
    hideLabel,
    tooltip,
    tooltipPlacement,
    icon,
    endIcon,
    iconSize,
    variant = 'diminished',
    borderRadius = 'sm',
    ...restProps
}: Readonly<TinyPressProps>) {
    const hoverTitle = hideLabel && !tooltip ? title : undefined;

    const tag = (
        <TinyPressRow type="button" $variant={variant} $borderRadius={borderRadius} title={hoverTitle} {...restProps}>
            <TinyButtonContent
                title={title}
                value={value}
                hideLabel={hideLabel}
                icon={icon}
                endIcon={endIcon}
                iconSize={iconSize}
            />
        </TinyPressRow>
    );

    return withTooltip(tag, tooltip, tooltipPlacement);
}

function TinyLinkButton({
    title,
    value,
    hideLabel,
    tooltip,
    tooltipPlacement,
    icon,
    iconSize,
    variant = 'diminished',
    borderRadius = 'sm',
    href,
    endIcon,
    ...restProps
}: Readonly<TinyLinkProps>) {
    const hoverTitle = hideLabel && !tooltip ? title : undefined;

    const tag = (
        <TinyLinkRow href={href} $variant={variant} $borderRadius={borderRadius} title={hoverTitle} {...restProps}>
            <TinyButtonContent
                title={title}
                value={value}
                hideLabel={hideLabel}
                icon={icon}
                endIcon={endIcon}
                iconSize={iconSize}
            />
        </TinyLinkRow>
    );

    return withTooltip(tag, tooltip, tooltipPlacement);
}

function TinyNavButton({
    title,
    value,
    hideLabel,
    tooltip,
    tooltipPlacement,
    icon,
    iconSize,
    endIcon,
    variant = 'diminished',
    borderRadius = 'sm',
    to,
    ...restProps
}: Readonly<TinyNavProps>) {
    const hoverTitle = hideLabel && !tooltip ? title : undefined;

    const tag = (
        <TinyNavRow to={to} $variant={variant} $borderRadius={borderRadius} title={hoverTitle} {...restProps}>
            <TinyButtonContent
                title={title}
                value={value}
                hideLabel={hideLabel}
                icon={icon}
                endIcon={endIcon}
                iconSize={iconSize}
            />
        </TinyNavRow>
    );

    return withTooltip(tag, tooltip, tooltipPlacement);
}

function TinyButtonContent({
    title,
    value,
    hideLabel,
    icon,
    iconSize,
    endIcon,
}: Readonly<Pick<TinyButtonCommonProps, 'title' | 'value' | 'hideLabel' | 'icon' | 'iconSize' | 'endIcon'>>) {
    return (
        <>
            {icon && <Icons weight="bold" name={icon} size={iconSize || 14} />}
            {hideLabel ? <SrOnlyText>{title}</SrOnlyText> : <TinyText>{title}</TinyText>}
            {value !== undefined && <BoldTinyText>{value}</BoldTinyText>}
            {endIcon && <Icons weight="bold" name={endIcon} size={iconSize || 14} />}
        </>
    );
}

function withTooltip(tag: ReactElement, tooltip?: string, tooltipPlacement?: TooltipProps['placement']) {
    if (!tooltip) return tag;

    return (
        <Tooltip title={tooltip} placement={tooltipPlacement} arrow>
            {tag}
        </Tooltip>
    );
}

const TinyPressRow = TinyButtonRow.withComponent('button') as unknown as ComponentType<TinyPressRowProps>;
const TinyLinkRow = TinyButtonRow.withComponent('a') as unknown as ComponentType<TinyLinkRowProps>;
const TinyNavRow = TinyButtonRow.withComponent(NavLink) as unknown as ComponentType<TinyNavRowProps>;
